import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import MainRadio from '../../MUI/MainRadio';
import FormSection from '../FormSection';
import PaymentReceivingMethodIcon from '../../../img/formSectionIcons/paymentReceivingMethod.svg';
import SelectWithValidation from '../../ValidationFormElements/SelectWithValidation';
import TextInputFieldWithValidation from '../../ValidationFormElements/TextInputFieldWithValidation';
import { TRANSFER_TYPES } from '../../../constants';
import useRegisterValidationFields from '../../../hooks/useRegisterValidationFields';
import useUnRegisterValidationFields from '../../../hooks/useUnRegisterValidationFields';

const TRANSIT_BANK_LABELS = ['vtb', 'ararat'];

const BankAccountForm = (props) => {
  const { formProps, transferTypes, banks, onChange = () => {} } = props;
  const { initialData = {} } = props;

  const { register, setValue, errors, clearError, unregister } = formProps;
  const { i18n } = useTranslation();
  const lang = i18n.language.toUpperCase();

  const [transferType, setTransferType] = useState(
    initialData.transferType || transferTypes[0].value,
  );
  const [selectedBank, setSelectedBank] = useState(initialData.transferBank || null);
  const [isBankSelectDisabled] = useState(transferType === 'account');

  useRegisterValidationFields(['transferType', 'transferBank'], register);
  useUnRegisterValidationFields(['transferType', 'transferBank'], unregister);

  useEffect(() => {
    setValue('transferType', transferType);
  }, [transferType, setValue]);

  useEffect(() => {
    setValue('transferBank', selectedBank || null, !!errors.transferBank);
  }, [selectedBank, setValue, errors.transferBank]);

  const banksByPrefix = useMemo(
    () => banks.reduce((acc, b) => {
      const copy = { ...acc };
      const prefixes = b.accountPrefix.split(',');
      prefixes.forEach((prefix) => {
        copy[prefix] = b;
      });
      return copy;
    }, {}),
    [banks],
  );

  const setBankFromAccount = useCallback(
    (account) => {
      if (account.length < 3) {
        setSelectedBank(null);
      } else {
        const prefix = account.slice(0, 3);
        const bank = banksByPrefix[prefix] || null;
        setSelectedBank((current) => {
          if (!current || current.id !== (bank && bank.id)) {
            return bank;
          }
          return current;
        });
      }
    },
    [setSelectedBank, banksByPrefix],
  );

  useEffect(() => {
    if (transferType === 'account' && initialData.transferAccount) {
      setBankFromAccount(initialData.transferAccount);
    }
  }, [initialData, setBankFromAccount, transferType]);

  const displayedBanks = useMemo(
    () => (transferType === 'transit'
      ? banks.filter((b) => TRANSIT_BANK_LABELS.includes(b.label))
      : banks),
    [banks, transferType],
  );

  return (
    <FormSection title="bankAccountForm.transferTypeLabel" icon={PaymentReceivingMethodIcon}>
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        {transferTypes.map(({ label, value }) => (
          <Grid item xs={12} key={label}>
            <MainRadio
              checked={value === transferType}
              value={value}
              onChange={({ e }) => {
                setTransferType(e.target.value);
                setSelectedBank(null);
                onChange();
              }}
              labelKey={label}
            />
          </Grid>
        ))}
      </Grid>
      <div>
        {transferType === TRANSFER_TYPES[1].value && (
          <Grid container spacing={4}>
            <Grid item md={6} xs={12}>
              <TextInputFieldWithValidation
                required
                formProps={formProps}
                labelKey="bankAccountForm.bankAccountLabel"
                name="transferAccount"
                value={initialData.transferAccount || ''}
                handleChange={(e) => {
                  setBankFromAccount(e.target.value);
                  onChange();
                }}
                register={register}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextInputFieldWithValidation
                formProps={formProps}
                labelKey="bankAccountForm.bankLabel"
                value={selectedBank ? selectedBank[`name${lang}`] : ''}
                disabled
                name="transferBank"
              />
            </Grid>
          </Grid>
        )}
        {transferType === TRANSFER_TYPES[0].value && (
          <Grid item xs={6}>
            <SelectWithValidation
              name="transferBank"
              label={isBankSelectDisabled || 'bankAccountForm.bankLabel'}
              value={selectedBank || ''}
              formProps={{
                errors,
                clearError,
              }}
              getMenuLabel={(option) => option.nameEN}
              options={displayedBanks}
              onChange={(bank) => {
                setSelectedBank(bank);
                onChange();
              }}
              disabled={isBankSelectDisabled}
            />
          </Grid>
        )}
      </div>
    </FormSection>
  );
};

BankAccountForm.propTypes = {
  transferTypes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  banks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      nameHY: PropTypes.string,
      nameRU: PropTypes.string,
      nameEN: PropTypes.string,
    }),
  ),
  formProps: PropTypes.shape({
    register: PropTypes.func.isRequired,
    unregister: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    errors: PropTypes.shape({
      transferBank: PropTypes.shape({}),
    }).isRequired,
    clearError: PropTypes.func.isRequired,
  }).isRequired,
  initialData: PropTypes.shape({
    transferType: PropTypes.string,
    transferBank: PropTypes.shape({}),
    transferAccount: PropTypes.string,
  }),
  onChange: PropTypes.func,
};

BankAccountForm.defaultProps = {
  transferTypes: TRANSFER_TYPES,
  banks: [],
  initialData: {},
  onChange: () => { },
};

export default BankAccountForm;
